import React from 'react'

export type TableColumn = {
  label: string
  sublabel?: string
}

export type Row = {
  label: string
  values: number[]
  total: number
  highlight?: string
  isPercentage?: boolean
  target?: number[]
}

type Props = {
  title: string
  color: string
  monthLabel: string
  showAllMonths: boolean
  selectedMonthIndex?: number
  customColumns?: TableColumn[]
  totalLabel?: string
  rows: Row[]
}

export default function Table({
  title,
  color,
  monthLabel,
  showAllMonths,
  selectedMonthIndex = 0,
  customColumns,
  totalLabel = 'TOTAL',
  rows,
}: Props) {
  const defaultMonths: TableColumn[] = [
    { label: 'JAN' },
    { label: 'FEV' },
    { label: 'MAR' },
    { label: 'ABR' },
    { label: 'MAI' },
    { label: 'JUN' },
    { label: 'JUL' },
    { label: 'AGO' },
    { label: 'SET' },
    { label: 'OUT' },
    { label: 'NOV' },
    { label: 'DEZ' },
  ]

  const columns = customColumns || (showAllMonths ? defaultMonths : [{ label: monthLabel.toUpperCase() }])
  const isMoney = title.includes('R$')

  const formatValue = (val: number, isPercentage?: boolean) => {
    if (isPercentage) {
      return (
        val.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + '%'
      )
    }
    if (isMoney) {
      return val.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
    }
    return val.toLocaleString('pt-BR')
  }

  const renderArrow = (val: number) => {
    if (val === 0) return null
    if (val > 0) return <span style={{ color: '#16a34a', marginRight: '5px', fontWeight: 'bold' }}>▲</span>
    return <span style={{ color: '#dc2626', marginRight: '5px', fontWeight: 'bold' }}>▼</span>
  }

  return (
    <div style={{ marginBottom: '20px' }}>
      <div
        style={{
          width: '100%',
          overflowX: 'auto',
          boxShadow: '0 0 10px #000000',
          borderRadius: '8px',
        }}
      >
        <table
          style={{
            width: '100%',
            minWidth: columns.length > 4 ? '1000px' : '100%',
            borderCollapse: 'collapse',
            fontFamily: 'Montserrat, Arial, sans-serif',
            fontSize: '11px',
            color: '#0f172a',
          }}
        >
          <thead>
            <tr style={{ backgroundColor: color, color: '#fff' }}>
              <th
                style={{
                  padding: '10px',
                  textAlign: 'left',
                  width: '170px',
                  whiteSpace: 'nowrap',
                }}
              >
                {title}
              </th>
              {columns.map((col, idx) => (
                <th key={idx} style={{ padding: '8px 10px', textAlign: 'center', whiteSpace: 'nowrap' }}>
                  <div>{col.label}</div>
                  {col.sublabel && (
                    <div style={{ fontSize: '9px', fontWeight: 'normal', opacity: 0.85, marginTop: '2px' }}>
                      {col.sublabel}
                    </div>
                  )}
                </th>
              ))}
              <th style={{ padding: '10px', textAlign: 'right', whiteSpace: 'nowrap' }}>{totalLabel}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => {
              const displayValues = customColumns
                ? row.values
                : showAllMonths
                ? row.values
                : [row.values.length === 1 ? row.values[0] : row.values[selectedMonthIndex] || 0]

              return (
                <tr key={idx}>
                  <td
                    style={{
                      padding: '9px 10px',
                      fontWeight: 'bold',
                      borderBottom: '1px solid #e2e8f0',
                      backgroundColor: row.highlight || '#f8fafc',
                      color: '#001842',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {row.label}
                  </td>
                  {displayValues.map((v, i) => (
                    <td
                      key={i}
                      style={{
                        padding: '9px 10px',
                        textAlign: 'end',
                        borderBottom: '1px solid #e2e8f0',
                        backgroundColor: row.highlight || '#ffffff',
                        color: '#0f172a',
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'end', color: '#0f172a' }}>
                        {row.isPercentage && renderArrow(v)}
                        {formatValue(v, row.isPercentage)}
                      </div>
                    </td>
                  ))}
                  <td
                    style={{
                      padding: '9px 10px',
                      textAlign: 'end',
                      fontWeight: 'bold',
                      borderBottom: '1px solid #e2e8f0',
                      backgroundColor: row.highlight || '#ffffff',
                      color: '#001842',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'end', color: '#001842' }}>
                      {row.isPercentage && renderArrow(row.total)}
                      {formatValue(row.total, row.isPercentage)}
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}
